import { useEffect, useMemo, useState } from 'react';
import { ComposableMap, Geographies, Geography } from 'react-simple-maps';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { TrendRangeTabs } from '@/components/TrendRangeTabs';
import { Globe } from 'lucide-react';

const GEO_URL = 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json';
const NO_DATA_FILL = '#e5e7eb';
const NO_DATA_STROKE = 'rgba(148,163,184,0.28)';
const HEAT_SCALES = {
  blue: ['#dbeafe', '#93c5fd', '#3b82f6', '#1d4ed8'],
  green: ['#dcfce7', '#86efac', '#22c55e', '#15803d'],
};

const REGION_META = {
  'North America': { color: '#6366f1', muted: 'bg-indigo-500/10 text-indigo-700 dark:text-indigo-300' },
  EMEA: { color: '#0f766e', muted: 'bg-teal-500/10 text-teal-700 dark:text-teal-300' },
  APAC: { color: '#d97706', muted: 'bg-amber-500/10 text-amber-700 dark:text-amber-300' },
  LATAM: { color: '#db2777', muted: 'bg-pink-500/10 text-pink-700 dark:text-pink-300' },
  Other: { color: '#64748b', muted: 'bg-slate-500/10 text-slate-700 dark:text-slate-300' },
};

const COUNTRY_REGION_MAP = {
  'United States of America': 'North America',
  'United States': 'North America',
  Canada: 'North America',
  Mexico: 'North America',
  Greenland: 'North America',
  Brazil: 'LATAM',
  Argentina: 'LATAM',
  Chile: 'LATAM',
  Colombia: 'LATAM',
  Peru: 'LATAM',
  Venezuela: 'LATAM',
  Ecuador: 'LATAM',
  Bolivia: 'LATAM',
  Paraguay: 'LATAM',
  Uruguay: 'LATAM',
  'Costa Rica': 'LATAM',
  Panama: 'LATAM',
  Guatemala: 'LATAM',
  Honduras: 'LATAM',
  Nicaragua: 'LATAM',
  'El Salvador': 'LATAM',
  Cuba: 'LATAM',
  'Dominican Republic': 'LATAM',
  Haiti: 'LATAM',
  'United Kingdom': 'EMEA',
  Ireland: 'EMEA',
  France: 'EMEA',
  Germany: 'EMEA',
  Spain: 'EMEA',
  Portugal: 'EMEA',
  Italy: 'EMEA',
  Netherlands: 'EMEA',
  Belgium: 'EMEA',
  Switzerland: 'EMEA',
  Austria: 'EMEA',
  Poland: 'EMEA',
  Sweden: 'EMEA',
  Norway: 'EMEA',
  Finland: 'EMEA',
  Denmark: 'EMEA',
  Turkey: 'EMEA',
  Israel: 'EMEA',
  'Saudi Arabia': 'EMEA',
  'United Arab Emirates': 'EMEA',
  'South Africa': 'EMEA',
  Egypt: 'EMEA',
  Nigeria: 'EMEA',
  Kenya: 'EMEA',
  Morocco: 'EMEA',
  India: 'APAC',
  China: 'APAC',
  Japan: 'APAC',
  'South Korea': 'APAC',
  Australia: 'APAC',
  'New Zealand': 'APAC',
  Singapore: 'APAC',
  Malaysia: 'APAC',
  Indonesia: 'APAC',
  Thailand: 'APAC',
  Vietnam: 'APAC',
  Philippines: 'APAC',
  Taiwan: 'APAC',
  Pakistan: 'APAC',
  Bangladesh: 'APAC',
};

const DEFAULT_REGIONS = ['North America', 'EMEA', 'APAC', 'LATAM'].map((region) => ({
  region,
  value: 0,
}));

const REGION_LABELS = {
  NA: 'North America',
  NAM: 'North America',
  NORTH_AMERICA: 'North America',
  'North America': 'North America',
  EMEA: 'EMEA',
  'Europe, Middle East & Africa': 'EMEA',
  APAC: 'APAC',
  'Asia Pacific': 'APAC',
  LATAM: 'LATAM',
  'Latin America': 'LATAM',
  Other: 'Other',
};

export function normalizeRegionName(region) {
  if (!region) return 'Other';
  return REGION_LABELS[region] || region;
}

export function getRegionForCountry(countryName) {
  return COUNTRY_REGION_MAP[countryName] || null;
}

function formatNumber(value) {
  return Number(value || 0).toLocaleString();
}

function getHeatColor(value, maxValue, colorScale = 'blue') {
  if (!value || value <= 0) return NO_DATA_FILL;
  const colors = HEAT_SCALES[colorScale] || HEAT_SCALES.blue;
  const ratio = maxValue > 0 ? value / maxValue : 0;
  if (ratio >= 0.75) return colors[3];
  if (ratio >= 0.45) return colors[2];
  if (ratio >= 0.2) return colors[1];
  return colors[0];
}

function normalizeCountryName(country) {
  if (country === 'United States') return 'United States of America';
  return country;
}

export function RegionalMapView({
  title = 'Regional Map',
  description,
  regions = [],
  countries = [],
  metricLabel = 'Activity',
  secondaryLabel,
  emptyMessage = 'Regional signals will appear as employees interact with Eva.',
  colorScale = 'blue',
  defaultView = 'country',
  timeRangeValue,
  onTimeRangeChange,
  loading = false,
}) {
  const [hoveredCountry, setHoveredCountry] = useState(null);
  const [mapView, setMapView] = useState(defaultView);
  const displayRegions = useMemo(() => {
    const sourceRegions = regions.length > 0 ? regions : DEFAULT_REGIONS;
    const byRegion = sourceRegions.reduce((acc, region) => {
      const regionName = normalizeRegionName(region.region);
      const existing = acc[regionName] || {
        ...region,
        region: regionName,
        value: 0,
        secondaryValue: 0,
      };

      existing.value = Number(existing.value || 0) + Number(region.value || 0);
      if (region.secondaryValue != null) {
        existing.secondaryValue = Number(existing.secondaryValue || 0) + Number(region.secondaryValue || 0);
      } else if (existing.secondaryValue === 0 && region.secondaryValue == null) {
        delete existing.secondaryValue;
      }
      acc[regionName] = existing;
      return acc;
    }, {});

    return Object.values(byRegion);
  }, [regions]);

  const regionByName = useMemo(() => displayRegions.reduce((acc, region) => {
    const regionName = normalizeRegionName(region.region);
    acc[regionName] = {
      ...region,
      region: regionName,
      value: Number(region.value || 0),
      secondaryValue: region.secondaryValue,
    };
    return acc;
  }, {}), [displayRegions]);

  const countryByName = useMemo(() => countries.reduce((acc, country) => {
    const countryName = normalizeCountryName(country.country || country.name);
    if (!countryName) return acc;
    const regionName = country.region
      ? normalizeRegionName(country.region)
      : getRegionForCountry(countryName) || 'Other';
    acc[countryName] = {
      ...country,
      country: countryName,
      value: Number(country.value || 0),
      secondaryValue: country.secondaryValue,
      region: regionName,
    };
    return acc;
  }, {}), [countries]);

  const countryItems = useMemo(() => Object.values(countryByName)
    .filter((country) => Number(country.value || 0) > 0)
    .sort((a, b) => Number(b.value || 0) - Number(a.value || 0)), [countryByName]);

  const hasCountryData = countryItems.length > 0;
  useEffect(() => {
    if (mapView === 'country' && !hasCountryData) {
      setMapView('region');
    }
  }, [hasCountryData, mapView]);

  const activeMapView = mapView === 'country' && hasCountryData ? 'country' : 'region';
  const activeItems = activeMapView === 'country' ? countryItems : displayRegions;
  const hasActiveData = activeItems.some((item) => Number(item.value || 0) > 0);
  const maxValue = Math.max(
    ...activeItems.map((item) => Number(item.value || 0)),
    1
  );

  const getCountryFill = (countryName) => {
    if (activeMapView === 'country') {
      const countryData = countryByName[countryName];
      const value = Number(countryData?.value || 0);
      return {
        fill: getHeatColor(value, maxValue, colorScale),
        opacity: value > 0 ? 1 : 0.88,
        interactive: value > 0,
        data: countryData,
      };
    }

    const region = getRegionForCountry(countryName);
    if (!region) return { fill: NO_DATA_FILL, opacity: 0.88, interactive: false };

    const value = Number(regionByName[region]?.value || 0);
    return {
      fill: getHeatColor(value, maxValue, colorScale),
      opacity: value > 0 ? 1 : 0.88,
      interactive: value > 0,
      data: regionByName[region],
    };
  };

  return (
    <Card className="bg-card border-border" data-testid="regional-map-view">
      <CardHeader className="pb-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <CardTitle className="text-lg font-semibold text-foreground flex items-center gap-2">
              <Globe className="w-5 h-5 text-teal-600" />
              {title}
            </CardTitle>
            {description && <p className="text-sm text-muted-foreground">{description}</p>}
          </div>
          <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
            {timeRangeValue && onTimeRangeChange && (
              <TrendRangeTabs
                value={timeRangeValue}
                onChange={onTimeRangeChange}
                className="w-full sm:min-w-[280px]"
              />
            )}
            <div className="inline-flex rounded-xl bg-secondary p-1" aria-label="Map level">
              {['region', 'country'].map((view) => (
                <button
                  key={view}
                  type="button"
                  aria-pressed={activeMapView === view}
                  disabled={view === 'country' && !hasCountryData}
                  onClick={() => setMapView(view)}
                  className={`rounded-lg px-3 py-1.5 text-sm font-medium capitalize transition ${
                    activeMapView === view
                      ? 'bg-background text-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground disabled:cursor-not-allowed disabled:opacity-45'
                  }`}
                >
                  {view}
                </button>
              ))}
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {loading ? (
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(260px,1fr)]" data-testid="regional-map-loading">
            <Skeleton className="aspect-[4/3] w-full rounded-2xl" />
            <div className="space-y-3">
              {[0, 1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-24 w-full rounded-xl" />
              ))}
            </div>
          </div>
        ) : (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(260px,1fr)]">
          <div className="relative overflow-hidden rounded-2xl border border-border bg-secondary/30 p-3 sm:p-4">
            <ComposableMap
              projection="geoNaturalEarth1"
              projectionConfig={{ scale: 155 }}
              className="h-auto w-full"
            >
              <Geographies geography={GEO_URL}>
                {({ geographies }) =>
                  geographies.map((geo) => {
                    const countryName = geo.properties?.name || geo.properties?.NAME || '';
                    const region = getRegionForCountry(countryName);
                    const { fill, opacity, interactive, data } = getCountryFill(countryName);

                    return (
                      <Geography
                        key={geo.rsmKey}
                        geography={geo}
                        onMouseEnter={() => {
                          if (!interactive) {
                            setHoveredCountry(null);
                            return;
                          }
                          setHoveredCountry({
                            country: countryName,
                            region: data?.region || region || 'Unassigned',
                            value: data?.value || 0,
                            secondaryValue: data?.secondaryValue,
                          });
                        }}
                        onMouseLeave={() => setHoveredCountry(null)}
                        style={{
                          default: {
                            fill,
                            fillOpacity: opacity,
                            stroke: interactive ? 'rgba(255,255,255,0.86)' : NO_DATA_STROKE,
                            strokeWidth: interactive ? 0.38 : 0.28,
                            outline: 'none',
                          },
                          hover: {
                            fill: interactive ? fill : NO_DATA_FILL,
                            fillOpacity: opacity,
                            stroke: interactive ? 'rgba(15,23,42,0.5)' : NO_DATA_STROKE,
                            strokeWidth: interactive ? 0.55 : 0.28,
                            outline: 'none',
                          },
                          pressed: { fill, fillOpacity: opacity, outline: 'none' },
                        }}
                      />
                    );
                  })
                }
              </Geographies>
            </ComposableMap>

            <div className="absolute bottom-3 left-3 max-w-[calc(100%-1.5rem)] rounded-xl border border-border bg-background/90 px-3 py-2 text-xs shadow-sm backdrop-blur">
              {hoveredCountry ? (
                <div>
                  <p className="font-semibold text-foreground">{hoveredCountry.country}</p>
                  <p className="text-muted-foreground">
                    {hoveredCountry.region} • {formatNumber(hoveredCountry.value)} {metricLabel.toLowerCase()}
                  </p>
                  {secondaryLabel && hoveredCountry.secondaryValue != null && (
                    <p className="text-muted-foreground">
                      {secondaryLabel}: {formatNumber(hoveredCountry.secondaryValue)}
                    </p>
                  )}
                </div>
              ) : (
                <p className="text-muted-foreground">
                  {activeMapView === 'country'
                    ? 'Countries with data light up on the map'
                    : 'Regions with data light up across their countries'}
                </p>
              )}
            </div>
          </div>

          <div className="space-y-3">
            {activeMapView === 'country' ? countryItems.map((country) => {
              const value = Number(country.value || 0);
              const heatColor = getHeatColor(value, maxValue, colorScale);
              return (
                <div key={country.country} className="rounded-xl border border-border bg-secondary/30 p-4">
                  <div className="mb-3 flex items-start justify-between gap-3">
                    <div>
                      <span className="font-medium text-foreground">
                        {country.country} · {country.region}
                      </span>
                    </div>
                    <span className="rounded-full bg-muted px-2 py-1 text-xs font-medium text-foreground">
                      {formatNumber(value)}
                    </span>
                  </div>
                  <div className="h-2 overflow-hidden rounded-full bg-background">
                    <div
                      className="h-full rounded-full"
                      style={{
                        width: `${value > 0 ? Math.max(4, (value / maxValue) * 100) : 0}%`,
                        backgroundColor: heatColor,
                      }}
                    />
                  </div>
                  <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
                    <span>{metricLabel}</span>
                    {secondaryLabel && country.secondaryValue != null && (
                      <span>{secondaryLabel} total: {formatNumber(country.secondaryValue)}</span>
                    )}
                  </div>
                </div>
              );
            }) : displayRegions.map((region) => {
              const meta = REGION_META[region.region] || REGION_META.Other;
              const value = Number(region.value || 0);
              const heatColor = getHeatColor(value, maxValue, colorScale);
              return (
                <div key={region.region} className="rounded-xl border border-border bg-secondary/30 p-4">
                  <div className="mb-3 flex items-center justify-between gap-3">
                    <span className="font-medium text-foreground">{region.region}</span>
                    <span className={`rounded-full px-2 py-1 text-xs font-medium ${value > 0 ? meta.muted : 'bg-muted text-muted-foreground'}`}>
                      {formatNumber(value)}
                    </span>
                  </div>
                  <div className="h-2 overflow-hidden rounded-full bg-background">
                    <div
                      className="h-full rounded-full"
                      style={{
                        width: `${value > 0 ? Math.max(4, (value / maxValue) * 100) : 0}%`,
                        backgroundColor: heatColor,
                      }}
                    />
                  </div>
                  <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
                    <span>{metricLabel}</span>
                    {secondaryLabel && region.secondaryValue != null && (
                      <span>{secondaryLabel}: {formatNumber(region.secondaryValue)}</span>
                    )}
                  </div>
                </div>
              );
            })}
            {!hasActiveData && (
              <div className="rounded-xl border border-dashed border-border bg-secondary/20 p-4 text-sm text-muted-foreground">
                {emptyMessage}
              </div>
            )}
          </div>
        </div>
        )}
      </CardContent>
    </Card>
  );
}
